import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, PlugZap, CircleCheck, CircleX } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSite } from "@/context/SiteContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  production_domain: "",
  wp_base_url: "",
  wp_username: "",
  wp_app_password: "",
  environment: "staging",
  default_mode: "draft",
};

export const AddSiteDialog = ({ open, onOpenChange }) => {
  const { refreshSites, setActiveSiteId } = useSite();
  const [form, setForm] = useState(EMPTY);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
    setTestResult(null);
  };

  const canTest = form.wp_base_url.length > 7 && form.wp_username && form.wp_app_password.length > 7;
  const canSave = canTest && form.name && form.production_domain;

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/sites/test-connection`, {
        wp_base_url: form.wp_base_url,
        wp_username: form.wp_username,
        wp_app_password: form.wp_app_password,
      });
      setTestResult(data);
    } catch (e) {
      setTestResult({ ok: false, error: "Request failed — check the URL." });
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/sites`, form);
      await refreshSites();
      setActiveSiteId(data.id);
      toast.success(`Workspace “${data.name}” added — now active.`);
      setForm(EMPTY);
      setTestResult(null);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save the site.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-site-dialog" className="sm:max-w-[480px] rounded-[14px]">
        <DialogHeader>
          <DialogTitle className="font-nunito font-extrabold text-[#383838]">Add Site Workspace</DialogTitle>
          <DialogDescription className="text-[#6e6e6e]">
            Connect a WordPress instance for deployment. Use a staging URL if the redesign shouldn’t touch production.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="as-name" className="text-xs font-nunito font-bold uppercase tracking-wide text-[#383838]">Site Name</Label>
              <Input id="as-name" data-testid="add-site-name-input" placeholder="Interact Marketing" value={form.name} onChange={set("name")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="as-domain" className="text-xs font-nunito font-bold uppercase tracking-wide text-[#383838]">Production Domain</Label>
              <Input id="as-domain" data-testid="add-site-domain-input" placeholder="interactmarketing.com" value={form.production_domain} onChange={set("production_domain")} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="as-url" className="text-xs font-nunito font-bold uppercase tracking-wide text-[#383838]">WP Deploy URL (staging or live)</Label>
            <Input id="as-url" data-testid="add-site-wp-url-input" placeholder="https://interactmarketing2.com" value={form.wp_base_url} onChange={set("wp_base_url")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="as-user" className="text-xs font-nunito font-bold uppercase tracking-wide text-[#383838]">WP Username</Label>
              <Input id="as-user" data-testid="add-site-username-input" autoComplete="off" value={form.wp_username} onChange={set("wp_username")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="as-pass" className="text-xs font-nunito font-bold uppercase tracking-wide text-[#383838]">Application Password</Label>
              <Input id="as-pass" data-testid="add-site-password-input" type="password" autoComplete="new-password" placeholder="xxxx xxxx xxxx xxxx" value={form.wp_app_password} onChange={set("wp_app_password")} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-nunito font-bold uppercase tracking-wide text-[#383838]">Environment</Label>
              <Select value={form.environment} onValueChange={set("environment")}>
                <SelectTrigger data-testid="add-site-environment-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staging">Staging</SelectItem>
                  <SelectItem value="live">Live</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-nunito font-bold uppercase tracking-wide text-[#383838]">Default Deploy Mode</Label>
              <Select value={form.default_mode} onValueChange={set("default_mode")}>
                <SelectTrigger data-testid="add-site-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="live">Live</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Button type="button" variant="outline" data-testid="add-site-test-connection-button" disabled={!canTest || testing} onClick={runTest} className="rounded-[10px] font-nunito text-xs font-bold uppercase tracking-wide">
              {testing ? <Loader2 size={13} className="animate-spin" /> : <PlugZap size={13} />}
              Test Connection
            </Button>
            {testResult && (
              <span data-testid="add-site-test-result" className={`flex items-center gap-1 text-xs ${testResult.ok ? "text-[#0050c0]" : "text-[#c25a00]"}`}>
                {testResult.ok ? <CircleCheck size={13} /> : <CircleX size={13} />}
                {testResult.ok
                  ? `Connected as ${testResult.user}${testResult.flatsome ? " · Flatsome ✓" : ""}`
                  : (testResult.error || "Failed").slice(0, 60)}
              </span>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" data-testid="add-site-cancel-button" onClick={() => onOpenChange(false)} className="rounded-[10px]">Cancel</Button>
          <Button type="button" data-testid="add-site-save-button" disabled={!canSave || saving} onClick={save} className="rounded-[10px] bg-[#0050c0] hover:bg-[#0343a0] text-white font-nunito font-extrabold uppercase tracking-wide text-xs">
            {saving ? <Loader2 size={13} className="animate-spin" /> : null}
            Save Workspace
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
